import {testIdProps} from '@github-ui/test-id-props'
import type {ActionListItemProps} from '@primer/react'

import {MemexColumnDataType} from '../api/columns/contracts/memex-column'
import type {UpdateOptions} from '../api/columns/contracts/single-select'
import type {CustomColumnKind} from '../api/columns/contracts/storage'
import {getColumnIcon, getColumnText} from '../components/column-detail-helpers'
import type {AddColumnRequest} from '../state-providers/columns/columns-state-provider'
import {Resources} from '../strings'
import {assertNever} from './assert-never'
import {createInitialIterations} from './iteration-builder'
import {durationToDays, type IterationDuration} from './iterations'
import {filterEmptyOptions} from './new-column'

export type ColumnDropdownItem = ActionListItemProps & {
  key: string
  leadingVisual: React.FunctionComponent<React.PropsWithChildren>
  text: string
  type: CustomColumnKind
}

type CreateFieldTypeItemsOptions = {
  includeMouseDownHandler?: boolean
}

type CreateApiRequestParams = {
  options: UpdateOptions
  iterationStartDate: Date
  iterationDuration: IterationDuration
}

const preventDefaultMouseDown = (e: React.MouseEvent) => e.preventDefault()

export function createFieldTypeItems(options: CreateFieldTypeItemsOptions = {}): Array<ColumnDropdownItem> {
  const {includeMouseDownHandler = false} = options

  const baseItems: Array<ColumnDropdownItem> = [
    {
      leadingVisual: getColumnIcon(MemexColumnDataType.Text),
      text: getColumnText(MemexColumnDataType.Text),
      key: 'Dropdown-text',
      type: MemexColumnDataType.Text,
      ...testIdProps('column-type-text'),
    },
    {
      leadingVisual: getColumnIcon(MemexColumnDataType.Number),
      text: getColumnText(MemexColumnDataType.Number),
      key: 'Dropdown-number',
      type: MemexColumnDataType.Number,
      ...testIdProps('column-type-number'),
    },
    {
      leadingVisual: getColumnIcon(MemexColumnDataType.Date),
      text: getColumnText(MemexColumnDataType.Date),
      key: 'Dropdown-date',
      type: MemexColumnDataType.Date,
      ...testIdProps('column-type-date'),
    },
    {
      leadingVisual: getColumnIcon(MemexColumnDataType.SingleSelect),
      text: getColumnText(MemexColumnDataType.SingleSelect),
      key: 'Dropdown-singleselect',
      type: MemexColumnDataType.SingleSelect,
      ...testIdProps('column-type-single-select'),
    },
    {
      leadingVisual: getColumnIcon(MemexColumnDataType.Iteration),
      text: getColumnText(MemexColumnDataType.Iteration),
      key: 'Dropdown-iteration',
      type: MemexColumnDataType.Iteration,
      ...testIdProps('column-type-iteration'),
    },
  ]

  if (includeMouseDownHandler) {
    return baseItems.map(item => ({
      ...item,
      onMouseDown: preventDefaultMouseDown,
    }))
  }

  return baseItems
}

export function createApiRequest(
  name: string,
  type: CustomColumnKind,
  params: CreateApiRequestParams,
): AddColumnRequest {
  const {options, iterationStartDate, iterationDuration} = params

  switch (type) {
    case MemexColumnDataType.SingleSelect: {
      const filteredOptions = filterEmptyOptions(options).map(({id, ...o}) => o)
      return {
        name,
        type,
        settings: filteredOptions.length > 0 ? {options: filteredOptions} : {},
      }
    }
    case MemexColumnDataType.Iteration: {
      const duration = durationToDays(iterationDuration)
      const startDay = iterationStartDate.getDay() !== 0 ? iterationStartDate.getDay() : 7
      const {iterations: activeIterations, completedIterations} = createInitialIterations(
        name,
        {duration, selectedStartDate: iterationStartDate},
        3,
      )
      return {
        name,
        type,
        settings: {
          configuration: {
            startDay,
            duration,
            iterations: activeIterations,
            completedIterations,
          },
        },
      }
    }
    case MemexColumnDataType.Number:
    case MemexColumnDataType.Text:
    case MemexColumnDataType.Date: {
      return {
        name,
        type,
        settings: undefined,
      }
    }
    default: {
      assertNever(type)
    }
  }
}

export function getDefaultColumnTitle(numUserDefinedColumns: number) {
  return `${Resources.newField}${numUserDefinedColumns > 0 ? ` ${numUserDefinedColumns + 1}` : ''}`
}
